import { useCallback, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router'

import { useAppDispatch } from 'common/hooks/redux'
import { debugPusher, useMultipleChannels } from 'features/Schedule/pusher/MultiplePusherProvider'

import Popup from 'common/components/Popup'
import { PUSHER_EVENTS } from 'features/Home/constants/infoConstants'
import {
  applyPatientDelete,
  applyPatientLocationUpdate,
  applyPatientStatusUpdate,
  applyPatientUpsert,
  applyRoomStatusUpdate,
  type ScheduleCacheContext,
} from 'features/Schedule/api/optimistics.updates'
import { isOwnScheduleOrigin } from 'features/Schedule/pusher/scheduleClientId'
import {
  isRedundantPusherPatientDelete,
  isRedundantPusherPatientStatus,
  isRedundantPusherPatientUpsert,
  isRedundantPusherRoomStatus,
  recordScheduleMutationUpsert,
} from 'features/Schedule/pusher/schedulePusherEcho'
import type {
  MassUpdateEvent,
  PatientDeleteEvent,
  PatientLocationUpdateEvent,
  PatientStatusUpdateEvent,
  PatientUpdateEvent,
  RoomStatusUpdateEvent,
} from 'features/Schedule/pusher/type'

export const useSchedulePusher = (date: string) => {
  const { channels, bookingIds } = useMultipleChannels()
  const { siteId } = useParams()
  const navigate = useNavigate()
  const scheduleState = useSelector((state: any) => state.schedule)
  const scheduleQueriesRef = useRef(scheduleState?.queries)

  const dispatch = useAppDispatch()
  const siteIdRef = useRef(siteId)
  const dateRef = useRef(date)

  useEffect(() => {
    scheduleQueriesRef.current = scheduleState?.queries
  }, [scheduleState?.queries])

  useEffect(() => {
    siteIdRef.current = siteId
    dateRef.current = date
  }, [siteId, date])

  const getCacheContext = useCallback((): ScheduleCacheContext | null => {
    const currentSiteId = siteIdRef.current
    const currentDate = dateRef.current
    if (!currentSiteId || !currentDate) return null

    return {
      siteId: currentSiteId,
      viewDate: currentDate,
      scheduleQueries: scheduleQueriesRef.current || {},
    }
  }, [])

  const massUpdate = useCallback(
    (event: MassUpdateEvent) => {
      if (debugPusher) console.log('MASS_UPDATE event received:', event)
      const { day, shouldUpdate } = event
      if (day === date && shouldUpdate) {
        Popup.showInfo(
          {
            title: 'Schedule updated',
            message: 'You have a mass update. Would you like to refresh the page?',
            mainButtonText: 'Refresh',
            secondButtonText: 'No',
          },
          () => navigate(0),
        )
      }
    },
    [date, navigate],
  )

  const patientUpsert = useCallback(
    (event: PatientUpdateEvent) => {
      const { data, shouldUpdate, origin_client_id } = event
      if (!shouldUpdate || !data) return

      const isOwn = isOwnScheduleOrigin(origin_client_id)
      if (isOwn === undefined && isRedundantPusherPatientUpsert(data)) return

      const context = getCacheContext()
      if (!context) return

      recordScheduleMutationUpsert(data)
      applyPatientUpsert(dispatch, context, data, { allowStructural: isOwn !== true })
    },
    [dispatch, getCacheContext],
  )

  const patientUpdate = useCallback(
    (event: PatientUpdateEvent) => {
      if (debugPusher) console.log('PATIENT_UPDATED event received:', event)
      patientUpsert(event)
    },
    [patientUpsert],
  )

  const patientCreate = useCallback(
    (event: PatientUpdateEvent) => {
      if (debugPusher) console.log('PATIENT_CREATE event received:', event)
      patientUpsert(event)
    },
    [patientUpsert],
  )

  const patientDelete = useCallback(
    (event: PatientDeleteEvent) => {
      if (debugPusher) console.log('PATIENT_DELETE event received:', event)
      const { appointment_id, shouldUpdate, origin_client_id } = event
      if (!shouldUpdate) return

      const appointmentId = Number(appointment_id)
      const isOwn = isOwnScheduleOrigin(origin_client_id)
      if (isOwn === true) return
      if (isOwn === undefined && isRedundantPusherPatientDelete(appointmentId)) return

      const context = getCacheContext()
      if (!context) return

      applyPatientDelete(dispatch, context, appointmentId)
    },
    [dispatch, getCacheContext],
  )

  const patientLocationUpdate = useCallback(
    (event: PatientLocationUpdateEvent) => {
      if (debugPusher) console.log('PATIENT_LOCATION_UPDATE event received:', event)
      const { appointment_id, room_id, shouldUpdate, origin_client_id } = event
      if (!shouldUpdate) return

      if (isOwnScheduleOrigin(origin_client_id) === true) return

      const context = getCacheContext()
      if (!context) return

      applyPatientLocationUpdate(dispatch, context, appointment_id, room_id)
    },
    [dispatch, getCacheContext],
  )

  const patientStatusUpdate = useCallback(
    (event: PatientStatusUpdateEvent) => {
      if (debugPusher) console.log('PATIENT_STATUS_UPDATE event received:', event)
      const { appointment_id, shouldUpdate, status_code, temperature, origin_client_id } = event
      if (!shouldUpdate) return

      const isOwn = isOwnScheduleOrigin(origin_client_id)
      if (
        isOwn === undefined &&
        isRedundantPusherPatientStatus(appointment_id, status_code, temperature)
      )
        return

      const context = getCacheContext()
      if (!context) return

      applyPatientStatusUpdate(dispatch, context, appointment_id, status_code, temperature)
    },
    [dispatch, getCacheContext],
  )

  const roomStatusUpdate = useCallback(
    (event: RoomStatusUpdateEvent) => {
      if (debugPusher) console.log('ROOM_STATUS_UPDATE event received:', event)
      const { room_id, room_status_code, shouldUpdate, origin_client_id } = event
      if (!shouldUpdate) return

      const isOwn = isOwnScheduleOrigin(origin_client_id)
      if (isOwn === undefined && isRedundantPusherRoomStatus(room_id, room_status_code)) return

      const context = getCacheContext()
      if (!context) return

      applyRoomStatusUpdate(dispatch, context, room_id, room_status_code)
    },
    [dispatch, getCacheContext],
  )

  useEffect(() => {
    if (!channels.size || !bookingIds.length) {
      if (debugPusher)
        console.log('No channels or bookingIds available for event binding', {
          channelsSize: channels.size,
          bookingIdsLength: bookingIds.length,
        })
      return
    }

    if (debugPusher) console.log('Binding events to channels, count:', channels.size)

    channels.forEach((channel, channelName) => {
      if (debugPusher) console.log(`Binding events to channel: ${channelName}`)

      channel.bind(PUSHER_EVENTS.MASS_UPDATE, massUpdate)
      channel.bind(PUSHER_EVENTS.PATIENT_UPDATED, patientUpdate)
      channel.bind(PUSHER_EVENTS.PATIENT_CREATE, patientCreate)
      channel.bind(PUSHER_EVENTS.PATIENT_DELETE, patientDelete)
      channel.bind(PUSHER_EVENTS.PATIENT_LOCATION_UPDATE, patientLocationUpdate)
      channel.bind(PUSHER_EVENTS.PATIENT_STATUS_UPDATE, patientStatusUpdate)
      channel.bind(PUSHER_EVENTS.ROOM_STATUS_UPDATE, roomStatusUpdate)
    })

    return () => {
      if (debugPusher) console.log('Unbinding events from channels')
      channels.forEach((channel, channelName) => {
        if (debugPusher) console.log(`Unbinding events from channel: ${channelName}`)

        channel.unbind(PUSHER_EVENTS.MASS_UPDATE, massUpdate)
        channel.unbind(PUSHER_EVENTS.PATIENT_UPDATED, patientUpdate)
        channel.unbind(PUSHER_EVENTS.PATIENT_CREATE, patientCreate)
        channel.unbind(PUSHER_EVENTS.PATIENT_DELETE, patientDelete)
        channel.unbind(PUSHER_EVENTS.PATIENT_LOCATION_UPDATE, patientLocationUpdate)
        channel.unbind(PUSHER_EVENTS.PATIENT_STATUS_UPDATE, patientStatusUpdate)
        channel.unbind(PUSHER_EVENTS.ROOM_STATUS_UPDATE, roomStatusUpdate)
      })
    }
  }, [
    channels,
    bookingIds.length,
    massUpdate,
    patientUpdate,
    patientCreate,
    patientDelete,
    patientLocationUpdate,
    patientStatusUpdate,
    roomStatusUpdate,
  ])
}
